import type { Lead, LeadStageType, ServiceType } from '@/context/LeadContext';
import { getCsrfToken } from '@/integrations/php/client';
import { parseLeadFormAnswers } from '@/utils/leadFormAnswers';

export type AdminLead = Lead & { isDuplicate: boolean };

export interface AdminLeadQuery {
  page?: number;
  perPage?: number;
  search?: string;
  stage?: string;
  service?: string;
  source?: string;
  campaign?: string;
  queue?: string;
  sort?: 'newest' | 'oldest';
  partnerId?: string;
  createdAfter?: string;
}

export interface LeadMetrics {
  summary: {
    total: number;
    new_leads: number;
    pending: number;
    information: number;
    success: number;
    unassigned: number;
    sms_sent: number;
    sms_failed: number;
    viewed: number;
    today: number;
    stale_pending: number;
    duplicates: number;
    avg_assignment_hours: number;
  };
  stage: Array<{ name: string; total: number }>;
  service: Array<{ name: string; total: number }>;
  source: Array<{ name: string; total: number }>;
  timeframe: Array<{ name: string; total: number }>;
  postal: Array<{ name: string; total: number }>;
  campaign: Array<{ name: string; total: number; success: number; pending: number; new_leads: number }>;
  partner: Array<{ id: string; name: string; total: number; success: number; pending: number; information: number; viewed: number; last_lead: string | null }>;
  monthly: Array<{ month: string; total: number; success: number }>;
  daily: Array<{ date: string; total: number; assigned: number; success: number }>;
  comparison: { current: number; previous: number };
  weekly_pulse: { current: number; previous: number };
  recent: Array<{
    id: string;
    name: string;
    service: string;
    stage: string;
    partner_id: string | null;
    partner_name: string | null;
    created_at: string;
  }>;
}

const apiHeaders = async (): Promise<Record<string, string>> => ({
  'X-CSRF-Token': await getCsrfToken(),
});

const toQuery = (query: AdminLeadQuery): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '' || value === 'all') return;
    const apiKey = key === 'perPage'
      ? 'per_page'
      : key === 'partnerId'
      ? 'partner_id'
      : key === 'createdAfter'
      ? 'created_after'
      : key;
    params.set(apiKey, String(value));
  });
  return params;
};

const nullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

const apiTimestamp = (isoValue: unknown, fallbackValue: unknown): string => {
  const value = nullableString(isoValue) || nullableString(fallbackValue);
  return value ? (value.includes('T') ? value : value.replace(' ', 'T')) : '';
};

export const mapAdminLead = (row: Record<string, unknown>): AdminLead => ({
  id: String(row.id),
  name: String(row.name || ''),
  phone: String(row.phone || ''),
  email: nullableString(row.email),
  address: String(row.address || ''),
  postalCode: String(row.postal_code || ''),
  service: (row.service || 'άλλο') as ServiceType,
  timeframe: String(row.timeframe || ''),
  comments: nullableString(row.comments) || undefined,
  formAnswers: parseLeadFormAnswers(row.form_answers),
  stage: (row.stage || 'Νέο') as LeadStageType,
  partnerId: nullableString(row.partner_id),
  partnerName: nullableString(row.partner_name),
  createdAt: apiTimestamp(row.created_at_iso, row.created_at),
  updatedAt: apiTimestamp(row.updated_at_iso, row.updated_at || row.created_at),
  smsStatus: row.sms_sent === null ? null : (Number(row.sms_sent) === 1 ? 'sent' : 'failed'),
  viewed: Number(row.link_opened || 0) === 1,
  viewedAt: nullableString(row.viewed_at),
  latitude: row.latitude === null ? null : Number(row.latitude),
  longitude: row.longitude === null ? null : Number(row.longitude),
  campaign: nullableString(row.campaign),
  source: nullableString(row.source),
  fbLeadId: nullableString(row.fb_lead_id),
  isDuplicate: Number(row.is_duplicate || 0) === 1,
});

export async function fetchAdminLeads(query: AdminLeadQuery = {}) {
  const response = await fetch(`/api/admin/leads.php?${toQuery(query)}`, {
    credentials: 'include',
    headers: await apiHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || 'Lead list failed');
  return {
    leads: Array.isArray(body.data) ? body.data.map(mapAdminLead) : [],
    pagination: body.pagination as { page: number; per_page: number; total: number; total_pages: number },
    campaigns: Array.isArray(body.filters?.campaigns) ? body.filters.campaigns as string[] : [],
  };
}

export async function fetchAdminLead(id: string): Promise<AdminLead> {
  const response = await fetch(`/api/lead-detail.php?id=${encodeURIComponent(id)}`, {
    credentials: 'include', headers: await apiHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body.data) throw new Error(body?.error?.message || 'Δεν φορτώθηκε η επαφή.');
  return mapAdminLead(body.data);
}

export async function fetchLeadMetrics(range = 'all', services: string[] = []): Promise<LeadMetrics> {
  const params = new URLSearchParams({ range });
  if (services.length > 0) params.set('services', services.join(','));
  const response = await fetch(`/api/admin/lead-metrics.php?${params}`, {
    credentials: 'include',
    headers: await apiHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || 'Lead metrics failed');

  const numberize = (row: Record<string, unknown>) => Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [key, key === 'name' || key === 'id' || key === 'month' || key === 'date' || key === 'last_lead' ? value : Number(value || 0)]),
  );
  return {
    ...body,
    summary: numberize(body.summary),
    stage: (body.stage || []).map(numberize),
    service: (body.service || []).map(numberize),
    source: (body.source || []).map(numberize),
    timeframe: (body.timeframe || []).map(numberize),
    postal: (body.postal || []).map(numberize),
    campaign: (body.campaign || []).map(numberize),
    partner: (body.partner || []).map(numberize),
    monthly: (body.monthly || []).map(numberize),
    daily: (body.daily || []).map(numberize),
    comparison: numberize(body.comparison),
    weekly_pulse: numberize(body.weekly_pulse),
    recent: body.recent || [],
  } as LeadMetrics;
}

export async function exportAdminLeadsCsv(query: AdminLeadQuery): Promise<void> {
  const params = toQuery(query);
  params.set('export', 'csv');
  const response = await fetch(`/api/admin/leads.php?${params}`, {
    credentials: 'include',
    headers: await apiHeaders(),
  });
  if (!response.ok) throw new Error('CSV export failed');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `diathermiki-leads-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface FollowUpReminderResult {
  status: string;
  days: number;
  eligible: number;
  sent?: number;
  errors?: number;
  total?: number;
  message?: string;
}

async function requestFollowUpReminders(days: number, preview: boolean): Promise<FollowUpReminderResult> {
  const response = await fetch('/api/send-followup-reminders.php', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...await apiHeaders(),
    },
    body: JSON.stringify({ days, preview }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || body?.error || 'Follow-up request failed');
  return {
    ...body,
    days: Number(body.days || days),
    eligible: Number(body.eligible ?? body.total ?? body.sent ?? 0),
    sent: body.sent === undefined ? undefined : Number(body.sent),
    errors: body.errors === undefined ? undefined : Number(body.errors),
    total: body.total === undefined ? undefined : Number(body.total),
  };
}

export const previewFollowUpReminders = (days = 3): Promise<FollowUpReminderResult> =>
  requestFollowUpReminders(days, true);

export const sendFollowUpReminders = (days = 3): Promise<FollowUpReminderResult> =>
  requestFollowUpReminders(days, false);
